import type { UserRole } from '../../users/models/users';
import type GetCurrentUserIdAndRoleResponseDto from './dto/GetCurrentUserIdAndRoleResponseDto';

import getApiClient from '../../../shared/api/api-client/getApiClient';
import ApiResultStatus from '../../../shared/api/api-result/ApiResultStatus';
import extractErrorMessageFromLicenseExpiringErrorResponseOriginalData from '../../license/expiring/extractErrorMessageFromLicenseExpiringErrorResponseOriginalData';
import extractTrackerIdFromLicenseExpiringErrorResponseOriginalData from '../../license/expiring/extractTrackerIdFromLicenseExpiringErrorResponseOriginalData';
import transformUserRoleFromDto from './dto/transformers/transformUserRoleFromDto';

const FORBIDDEN_HTTP_CODE = 403;
const LICENSE_EXPIRED_HTTP_CODE = 402;

export default async function getCurrentUserIdAndRole(): Promise<
  | {
      errorMessage: string;
      status: ApiResultStatus.ACCESS_DENIED;
    }
  | {
      errorMessage: string;
      status: ApiResultStatus.ERROR;
    }
  | {
      errorMessage: string;
      status: ApiResultStatus.LICENSE_EXPIRED;
    }
  | {
      id: string;
      role: UserRole;
      status: ApiResultStatus.OK;
    }
> {
  const client = getApiClient();

  const response = await client.get('/api/user/role');

  if (response.ok) {
    const json = await response.json<GetCurrentUserIdAndRoleResponseDto>();

    return {
      id: String(json.id),
      role: transformUserRoleFromDto(json.role),
      status: ApiResultStatus.OK,
    };
  }

  const json = await response.json<{
    message?: string;
  }>();

  if (response.status === FORBIDDEN_HTTP_CODE || json.message?.includes('AUTHENTICATION_ERROR')) {
    return {
      errorMessage: await response.errorMessage(),
      status: ApiResultStatus.ACCESS_DENIED,
    };
  }

  if (response.status === LICENSE_EXPIRED_HTTP_CODE) {
    const errorMessage = extractErrorMessageFromLicenseExpiringErrorResponseOriginalData(json) ?? '';
    const trackerId = extractTrackerIdFromLicenseExpiringErrorResponseOriginalData(json);

    return {
      errorMessage: `${errorMessage}\n\nTracker id: ${trackerId}`,
      status: ApiResultStatus.LICENSE_EXPIRED,
    };
  }

  return {
    errorMessage: await response.errorMessage(),
    status: ApiResultStatus.ERROR,
  };
}
